import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  ArrowLeft, LogOut, Clock, CheckCircle, FileText, Package, ExternalLink, ChevronRight, RefreshCw, MessageSquare
} from "lucide-react";
import TicketChat from "../components/TicketChat";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
import Logo from "../components/Logo";

const STAGES = [
  { key: "inquiry_submitted", label: "Inquiry Submitted", icon: FileText, desc: "Your request has been received and logged in our system." },
  { key: "under_review", label: "Under Review", icon: Clock, desc: "Our team is reviewing your needs and matching you with providers." },
  { key: "plan_confirmed", label: "Plan Confirmed", icon: Package, desc: "Your treatment plan, schedule, and pricing are finalized." },
  { key: "fully_booked", label: "Fully Booked", icon: CheckCircle, desc: "All services are locked in. Your journey is confirmed!" },
];

const STATUS_COLOR = {
  inquiry_submitted: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  under_review: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  plan_confirmed: { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  fully_booked: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
};

function StatusBadge({ status }) {
  const col = STATUS_COLOR[status] || STATUS_COLOR.inquiry_submitted;
  const stage = STAGES.find(s => s.key === status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${col.bg} ${col.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
      {stage?.label || status}
    </span>
  );
}

function StatusTracker({ status }) {
  const currentIdx = STAGES.findIndex(s => s.key === status);
  return (
    <div className="relative">
      {/* Progress line */}
      <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-200 hidden sm:block">
        <div
          className="h-full bg-gradient-to-r from-[#3FA7D6] to-[#0B3C5D] transition-all duration-700"
          style={{ width: `${(currentIdx / (STAGES.length - 1)) * 100}%` }}
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 relative">
        {STAGES.map((stage, idx) => {
          const done = idx <= currentIdx;
          const active = idx === currentIdx;
          const Icon = stage.icon;
          return (
            <div key={stage.key} className="flex flex-col items-center text-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                done ? "bg-[#0B3C5D] text-white shadow-[0_0_0_4px_rgba(63,167,214,0.2)]" : "bg-gray-200 text-gray-400"
              } ${active ? "ring-2 ring-[#3FA7D6] ring-offset-2" : ""}`}>
                {idx < currentIdx ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <p className={`text-xs font-semibold mt-2 ${done ? "text-[#0B3C5D]" : "text-gray-400"}`}>{stage.label}</p>
              {active && <p className="text-xs text-[#6B7280] mt-1 hidden sm:block max-w-[100px]">{stage.desc}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TicketCard({ ticket }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("details"); // "details" | "chat"
  const hasPlan = ticket.treatment_details || ticket.pricing || ticket.travel_info || ticket.accommodation || ticket.provider || ticket.timeline;

  return (
    <div className="glass-card rounded-3xl overflow-hidden hover-lift" data-testid={`ticket-card-${ticket.ticket_id}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#0B3C5D] flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-black text-[#0B3C5D] font-outfit text-lg" data-testid="ticket-id">{ticket.ticket_id}</span>
                <StatusBadge status={ticket.status} />
              </div>
              <p className="text-sm text-[#6B7280] mt-0.5">{ticket.treatment} · Submitted {new Date(ticket.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-sm font-semibold text-[#3FA7D6] hover:text-[#0B3C5D] transition-colors">
            {open ? "Hide Details" : "View Details"} <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
        </div>

        {/* Progress Tracker */}
        <div className="mt-5 pt-5 border-t border-gray-50">
          <StatusTracker status={ticket.status} />
        </div>
      </div>

      {/* Expandable Details */}
      {open && (
        <div className="border-t border-gray-100">
          {/* Tabs */}
          <div className="flex">
            {[["details", "Details"], ["chat", "Messages"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === k ? "text-[#0B3C5D] border-b-2 border-[#0B3C5D] bg-white" : "text-[#6B7280] bg-[#F8FAFC] hover:text-[#0B3C5D]"}`}>
                {k === "chat" && <MessageSquare className="w-3.5 h-3.5" />} {l}
              </button>
            ))}
          </div>

          {tab === "details" && (
            <div className="p-6 bg-[#F8FAFC]">
          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            <InfoBlock label="Your Name" value={ticket.name} />
            <InfoBlock label="Email" value={ticket.email} />
            <InfoBlock label="Phone" value={ticket.phone} />
            <InfoBlock label="Treatment" value={ticket.treatment} />
            {ticket.message && <InfoBlock label="Your Message" value={ticket.message} className="sm:col-span-2" />}
          </div>

          {hasPlan && (
            <div className="border-t border-gray-200 pt-5 mt-2">
              <h4 className="font-bold text-[#0B3C5D] font-outfit mb-4 flex items-center gap-2">
                <Package className="w-4 h-4 text-[#3FA7D6]" /> Your Confirmed Plan
              </h4>
              <div className="grid sm:grid-cols-2 gap-4">
                {ticket.provider && <InfoBlock label="Medical Provider" value={ticket.provider} accent />}
                {ticket.pricing && <InfoBlock label="Pricing Breakdown" value={ticket.pricing} accent />}
                {ticket.treatment_details && <InfoBlock label="Treatment Details" value={ticket.treatment_details} accent className="sm:col-span-2" />}
                {ticket.travel_info && <InfoBlock label="Travel Arrangements" value={ticket.travel_info} accent />}
                {ticket.accommodation && <InfoBlock label="Accommodation" value={ticket.accommodation} accent />}
                {ticket.timeline && <InfoBlock label="Timeline & Schedule" value={ticket.timeline} accent className="sm:col-span-2" />}
              </div>
            </div>
          )}

          {!hasPlan && ticket.status === "under_review" && (
            <div className="border-t border-gray-200 pt-4 mt-2">
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <RefreshCw className="w-5 h-5 text-amber-500 animate-spin" />
                <div>
                  <p className="font-semibold text-amber-800 text-sm">Under Review</p>
                  <p className="text-xs text-amber-600">Our team is preparing your personalized plan. You'll see full details here once confirmed.</p>
                </div>
              </div>
            </div>
          )}
          </div>
          )}

          {tab === "chat" && (
            <div className="p-4">
              <TicketChat ticketId={ticket.ticket_id} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoBlock({ label, value, accent, className = "" }) {
  return (
    <div className={`glass-panel p-4 ${className}`}>
      <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-sm font-medium ${accent ? "text-[#0B3C5D]" : "text-gray-700"} whitespace-pre-wrap`}>{value}</p>
    </div>
  );
}

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const res = await axios.get(`${API}/user/tickets`, { withCredentials: true });
        setTickets(res.data);
      } catch (err) {
        console.error("Failed to fetch tickets:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top Bar */}
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Logo />
            </Link>
            <div className="hidden sm:block w-px h-5 bg-gray-200" />
            <span className="hidden sm:block text-sm text-[#6B7280]">My Health Journey</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-[#3FA7D6]" />
            )}
            <span className="text-sm font-medium text-[#0B3C5D] hidden sm:block">{user?.name}</span>
            <Link to="/" className="text-xs text-[#6B7280] hover:text-[#3FA7D6] transition-colors flex items-center gap-1">
              <ExternalLink className="w-3.5 h-3.5" /> Site
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-red-500 transition-colors" data-testid="user-logout-button">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="max-w-[1100px] mx-auto px-6 py-10">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-[#0B3C5D] font-outfit tracking-tight" data-testid="user-dashboard-title">
            Welcome back, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-[#6B7280] mt-1">Track your medical journey and manage all your services below.</p>
        </div>

        {/* Tickets */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#3FA7D6] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !Array.isArray(tickets) || tickets.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-testid="no-tickets-message">
            <div className="w-16 h-16 rounded-full bg-[#3FA7D6]/10 flex items-center justify-center mx-auto mb-5">
              <FileText className="w-8 h-8 text-[#3FA7D6]" />
            </div>
            <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit mb-2">No Inquiries Yet</h3>
            <p className="text-[#6B7280] mb-7">Submit a consultation request to start your health journey.</p>
            <Link to="/contact" className="btn-primary inline-flex" data-testid="start-journey-btn">
              Get Free Consultation <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-sm text-[#6B7280]">{tickets.length} {tickets.length !== 1 ? "inquiries" : "inquiry"} found</p>
            {tickets.map(ticket => (
              <TicketCard key={ticket.ticket_id} ticket={ticket} />
            ))}
          </div>
        )}

        {/* CTA to submit new */}
        {tickets.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-[#0B3C5D] to-[#124b70] rounded-3xl p-8 text-center shadow-[0_12px_40px_rgba(11,60,93,0.2)] border border-[#3FA7D6]/20 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#3FA7D6]/20 rounded-full blur-3xl mix-blend-screen pointer-events-none" />
            <h3 className="text-xl font-bold text-white font-outfit mb-2">Need Another Treatment?</h3>
            <p className="text-white/70 text-sm mb-5">Submit a new inquiry and track it right here in your dashboard.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#3FA7D6] text-white font-semibold rounded-full hover:bg-white hover:text-[#0B3C5D] transition-all duration-200">
              New Inquiry <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
